// Seller Storefront (v2) — client behavior.
// ----------------------------------------------------------------
// Tabs (Items / Reviews / About / Shop Policies), sub-section
// filtering, sort, search-within-shop, featured carousel arrows,
// Follow toggle, and the shop-level Yotpo aggregation (rating +
// review count + recent review list) that backs the Reviews tab.
//
// The aggregation runs client-side using the same Yotpo app key
// pdp-reviews-redesign.js already uses — no new metafields are
// required. When the back-end ships cjx.store_avg_rating /
// cjx.store_review_count metafields, the section's data block can
// surface those and this client call becomes a refinement.

(function () {
  'use strict';

  var YOTPO_REVIEWS_TOPN = 12;    // fetch reviews across the top N most-reviewed products
  var YOTPO_REVIEWS_LIMIT = 24;   // hard cap on rendered reviews

  function init() {
    var page = document.querySelector('[data-seller-page]');
    if (!page) return;

    var dataEl = document.getElementById('seller-page-data');
    var data = {};
    if (dataEl) {
      try { data = JSON.parse(dataEl.textContent || dataEl.innerHTML); } catch (_) {}
    }

    setupTabs(page);
    setupItems(page);
    setupFeaturedCarousel(page);
    setupSearchWithinShop(page);
    setupContactSellerDrawer();
    setupAnalytics(page, data);
    if (data && Array.isArray(data.products) && data.products.length) {
      setupShopReviews(page, data);
    }
  }

  // ── ANALYTICS ──────────────────────────────────────
  // Funnel events for the seller storefront: entry, engagement,
  // high-intent micro-conversion. Sent to BOTH gtag (GA4) and
  // clarity (Microsoft Clarity, for session-replay correlation).
  // Both globals are loaded sitewide in header.liquid but are
  // async — defensive typeof checks so a slow loader can't break
  // the page. Same event names across both platforms so the data
  // is correlatable in Clarity's GA4 integration.
  function setupAnalytics(page, data) {
    var props = {
      seller_store_id: (data && data.storeId) || '',
      seller_store_handle: (data && data.storeHandle) || '',
      seller_store_name: (data && data.storeName) || '',
      seller_product_count: (data && data.products) ? data.products.length : 0
    };

    function track(name, extra) {
      var payload = extra ? Object.assign({}, props, extra) : props;
      try {
        if (typeof window.gtag === 'function') {
          window.gtag('event', name, payload);
        } else if (window.dataLayer && typeof window.dataLayer.push === 'function') {
          window.dataLayer.push(Object.assign({ event: name }, payload));
        }
      } catch (_) {}
      try {
        if (typeof window.clarity === 'function') {
          window.clarity('event', name);
        }
      } catch (_) {}
    }

    // Clarity custom tags — these become filters in the dashboard,
    // so you can carve session replays down to "people who landed
    // on a seller page" or "people on TheraBox specifically".
    try {
      if (typeof window.clarity === 'function') {
        window.clarity('set', 'page_type', 'seller_storefront');
        if (props.seller_store_handle) {
          window.clarity('set', 'seller_handle', String(props.seller_store_handle));
        }
      }
    } catch (_) {}

    // Entry event — fires once per page load.
    track('seller_page_view');

    // Engagement event — delegated on the items grid + featured carousel.
    // Captures clicks anywhere inside a product card (image, title, price).
    document.addEventListener('click', function (e) {
      var card = e.target.closest && e.target.closest('.seller-card');
      if (!card || !page.contains(card)) return;
      // Don't double-fire on the wishlist heart (it preventDefaults).
      if (e.target.closest('[data-seller-card-heart]')) return;
      track('seller_product_click', {
        product_handle: (card.getAttribute('href') || '').split('/').pop().split('?')[0],
        product_title: card.getAttribute('aria-label') || '',
        product_price: card.dataset.price || ''
      });
    });

    // High-intent micro-conversion — fires whenever the contact-seller
    // drawer is opened from this page (either CTA: header button OR
    // About-tab "Message this seller" link).
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest && e.target.closest('[data-app-contact-seller-open]');
      if (!trigger || !page.contains(trigger)) return;
      track('seller_message_opened', {
        trigger_location: trigger.closest('.seller-identity__actions') ? 'identity_cta'
          : trigger.closest('.seller-about__contact') ? 'about_tab'
          : 'other'
      });
    });
  }

  // ── CONTACT SELLER DRAWER ──────────────────────────
  // The pl-pdp-contact-seller-form.liquid snippet renders the drawer
  // but its open/close handler lives in dist.product.js / pdp-reviews-
  // redesign.js — neither loads on the seller template. Same delegated
  // pattern as pdp-reviews-redesign.js: idempotent class toggle, plus
  // publish() to the legacy event bus when available.
  function setupContactSellerDrawer() {
    if (window.__sellerContactDrawerBound) return;
    window.__sellerContactDrawerBound = true;

    function openForm() {
      var app = window.app;
      if (app && app.event) {
        try { app.event('pdp-contact-seller-open').publish(); } catch (_) {}
      }
      var form = document.getElementById('pdp-contact-seller');
      var backdrop = document.getElementById('modal');
      if (form) form.classList.remove('inactive');
      if (backdrop) backdrop.classList.remove('inactive');
      document.body.classList.add('seller-drawer-open');
    }
    function closeForm() {
      var form = document.getElementById('pdp-contact-seller');
      var backdrop = document.getElementById('modal');
      if (form) form.classList.add('inactive');
      if (backdrop) backdrop.classList.add('inactive');
      document.body.classList.remove('seller-drawer-open');
    }

    document.addEventListener('click', function (e) {
      var trigger = e.target.closest && e.target.closest('[data-app-contact-seller-open]');
      if (!trigger) return;
      e.preventDefault();
      openForm();
    });
    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('#pdp-contact-seller-form-close-btn')) closeForm();
      var backdrop = document.getElementById('modal');
      if (backdrop && e.target === backdrop) closeForm();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeForm();
    });
  }

  // ── TABS ────────────────────────────────────────────
  function setupTabs(page) {
    var tabs = Array.prototype.slice.call(page.querySelectorAll('[data-seller-tab]'));
    var panes = Array.prototype.slice.call(page.querySelectorAll('[data-seller-pane]'));
    if (!tabs.length || !panes.length) return;

    function activate(target) {
      tabs.forEach(function (t) {
        t.classList.toggle('seller-tab--active', t.dataset.target === target);
      });
      panes.forEach(function (p) {
        p.classList.toggle('seller-pane--active', p.dataset.sellerPane === target);
      });
      // Anchor for browser back/forward + share-friendly deep linking.
      history.replaceState(null, '', '#' + target);
    }

    tabs.forEach(function (t) {
      t.addEventListener('click', function () { activate(t.dataset.target); });
    });

    // Deep link from URL hash.
    var hash = (window.location.hash || '').replace('#', '');
    if (hash && tabs.some(function (t) { return t.dataset.target === hash; })) {
      activate(hash);
    }
  }

  // ── ITEMS: sub-section / price / sort / quick filters ─
  function setupItems(page) {
    var grid = page.querySelector('[data-seller-items-grid]');
    if (!grid) return;

    var cards = Array.prototype.slice.call(grid.querySelectorAll('[data-seller-product-card]'));
    var sectionBtns = Array.prototype.slice.call(page.querySelectorAll('[data-section]'));
    var quickChips = Array.prototype.slice.call(page.querySelectorAll('[data-quick]'));
    var priceSelect = page.querySelector('[data-seller-price-filter]');
    var sortSelect = page.querySelector('[data-seller-sort]');
    var emptyEl = page.querySelector('[data-seller-items-empty]');
    var countEl = page.querySelector('[data-seller-items-count]');
    var titleEl = page.querySelector('[data-seller-items-title]');

    var state = { section: 'all', sectionKind: 'all', price: 'any', sort: 'featured', search: '', quick: '' };

    function cardMatchesSection(card) {
      if (state.section === 'all') return true;
      if (state.sectionKind === 'type') {
        return (card.dataset.type || '') === state.section;
      }
      if (state.sectionKind === 'status') {
        if (state.section === 'on-sale') return (card.dataset.onSale || '') === 'true';
        var flair = '|' + (card.dataset.flair || '') + '|';
        return flair.indexOf('|' + state.section + '|') !== -1;
      }
      // tag kind (legacy — left for future use)
      var tags = '|' + (card.dataset.tags || '') + '|';
      return tags.indexOf('|' + state.section + '|') !== -1;
    }
    function cardMatchesQuick(card) {
      if (!state.quick) return true;
      if (state.quick === 'on-sale') return (card.dataset.onSale || '') === 'true';
      var flair = '|' + (card.dataset.flair || '') + '|';
      return flair.indexOf('|' + state.quick + '|') !== -1;
    }

    function applyFilters() {
      var visible = 0;
      cards.forEach(function (card) {
        var price = parseFloat(card.dataset.price || '0');
        var title = card.dataset.title || '';

        var matchSection = cardMatchesSection(card);
        var matchPrice = state.price === 'any'
          || (state.price === 'under50' && price < 50)
          || (state.price === '50-100' && price >= 50 && price < 100)
          || (state.price === '100+' && price >= 100);
        var matchSearch = !state.search || title.indexOf(state.search) !== -1;
        var matchQuick = cardMatchesQuick(card);

        var show = matchSection && matchPrice && matchSearch && matchQuick;
        card.style.display = show ? '' : 'none';
        if (show) visible++;
      });

      var visibleCards = cards.filter(function (c) { return c.style.display !== 'none'; });
      var cmp = {
        featured: function () { return 0; },
        'price-asc': function (a, b) { return parseFloat(a.dataset.price) - parseFloat(b.dataset.price); },
        'price-desc': function (a, b) { return parseFloat(b.dataset.price) - parseFloat(a.dataset.price); },
        rating: function (a, b) { return parseFloat(b.dataset.rating || 0) - parseFloat(a.dataset.rating || 0); },
        reviews: function (a, b) { return parseInt(b.dataset.reviews || 0, 10) - parseInt(a.dataset.reviews || 0, 10); }
      }[state.sort] || function () { return 0; };
      visibleCards.sort(cmp).forEach(function (c) { grid.appendChild(c); });

      if (emptyEl) emptyEl.hidden = visible > 0;
      if (countEl) countEl.textContent = visible + ' items';
      if (titleEl) {
        if (state.section === 'all' && !state.quick) {
          titleEl.textContent = 'All items';
        } else if (state.section !== 'all') {
          var btn = sectionBtns.filter(function (b) { return b.dataset.section === state.section; })[0];
          titleEl.textContent = btn && btn.querySelector('span') ? btn.querySelector('span').textContent : 'Items';
        } else if (state.quick) {
          var chip = quickChips.filter(function (c) { return c.dataset.quick === state.quick; })[0];
          titleEl.textContent = chip ? chip.textContent.trim() : 'Items';
        }
      }
    }

    // Auto-populate per-section counts.
    //   - type / status sections hide at count = 0 (always meaningful when ≥1)
    //   - tag sections hide at count < 3, then keep top 8 by count (rest hide)
    //     — a tag matching 1-2 items is a label, not a category, and an
    //     unbounded list of 30+ tags drowns out real navigation.
    var tagCandidates = [];
    sectionBtns.forEach(function (b) {
      if (b.dataset.section === 'all') return;
      var countSlot = b.querySelector('[data-section-count]');
      if (!countSlot) return;
      var kind = b.dataset.sectionKind || 'tag';
      var key = b.dataset.section;
      var n = 0;
      cards.forEach(function (card) {
        if (kind === 'type') {
          if ((card.dataset.type || '') === key) n++;
        } else if (kind === 'status') {
          if (key === 'on-sale') { if ((card.dataset.onSale || '') === 'true') n++; return; }
          var flair = '|' + (card.dataset.flair || '') + '|';
          if (flair.indexOf('|' + key + '|') !== -1) n++;
        } else {
          var tags = '|' + (card.dataset.tags || '') + '|';
          if (tags.indexOf('|' + key + '|') !== -1) n++;
        }
      });
      if (kind === 'tag') {
        tagCandidates.push({ btn: b, slot: countSlot, n: n });
        return; // decide visibility after sort below
      }
      if (n < 1) {
        b.parentElement && (b.parentElement.style.display = 'none');
      } else {
        countSlot.textContent = n;
      }
    });
    tagCandidates
      .sort(function (a, b) { return b.n - a.n; })
      .forEach(function (c, idx) {
        if (c.n < 3 || idx >= 8) {
          c.btn.parentElement && (c.btn.parentElement.style.display = 'none');
        } else {
          c.slot.textContent = c.n;
        }
      });

    sectionBtns.forEach(function (b) {
      b.addEventListener('click', function () {
        state.section = b.dataset.section;
        state.sectionKind = b.dataset.sectionKind || 'all';
        sectionBtns.forEach(function (x) { x.classList.toggle('seller-section-btn--active', x === b); });
        applyFilters();
      });
    });

    quickChips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        var key = chip.dataset.quick;
        if (state.quick === key) {
          state.quick = '';
          chip.classList.remove('is-active');
        } else {
          state.quick = key;
          quickChips.forEach(function (c) { c.classList.toggle('is-active', c === chip); });
        }
        applyFilters();
      });
    });

    // Hide quick chips that match zero cards — no point clicking a dead chip
    quickChips.forEach(function (chip) {
      var key = chip.dataset.quick;
      var n = 0;
      cards.forEach(function (card) {
        if (key === 'on-sale') { if ((card.dataset.onSale || '') === 'true') n++; return; }
        var flair = '|' + (card.dataset.flair || '') + '|';
        if (flair.indexOf('|' + key + '|') !== -1) n++;
      });
      if (n === 0) chip.style.display = 'none';
    });

    if (priceSelect) priceSelect.addEventListener('change', function () { state.price = priceSelect.value; applyFilters(); });
    if (sortSelect) sortSelect.addEventListener('change', function () { state.sort = sortSelect.value; applyFilters(); });

    page._sellerApplySearch = function (q) { state.search = (q || '').toLowerCase().trim(); applyFilters(); };
  }

  // ── FEATURED CAROUSEL ARROWS ────────────────────────
  function setupFeaturedCarousel(page) {
    var track = page.querySelector('[data-seller-featured-track]');
    var prevs = page.querySelectorAll('[data-seller-featured-prev]');
    var nexts = page.querySelectorAll('[data-seller-featured-next]');
    if (!track || !prevs.length || !nexts.length) return;
    var step = 320;
    prevs.forEach(function (btn) {
      btn.addEventListener('click', function () { track.scrollBy({ left: -step, behavior: 'smooth' }); });
    });
    nexts.forEach(function (btn) {
      btn.addEventListener('click', function () { track.scrollBy({ left: step, behavior: 'smooth' }); });
    });
  }

  // ── SEARCH WITHIN SHOP ──────────────────────────────
  function setupSearchWithinShop(page) {
    var input = page.querySelector('[data-seller-search]');
    if (!input) return;
    var t = null;
    input.addEventListener('input', function () {
      clearTimeout(t);
      t = setTimeout(function () {
        if (page._sellerApplySearch) page._sellerApplySearch(input.value);
        // If user starts typing, snap to the Items tab.
        var itemsTab = page.querySelector('[data-seller-tab][data-target="items"]');
        if (input.value && itemsTab && !itemsTab.classList.contains('seller-tab--active')) {
          itemsTab.click();
        }
      }, 160);
    });
  }

  // ── SHOP-LEVEL YOTPO AGGREGATION ───────────────────
  function setupShopReviews(page, data) {
    var key = data.yotpoAppKey;
    if (!key) return;

    // Pick the top-N most-reviewed products to fetch reviews from. We
    // sum their review counts + weight-average their scores to get a
    // shop-level rating with no per-shop metafield required.
    var pool = data.products
      .filter(function (p) { return p.reviewCount > 0; })
      .sort(function (a, b) { return b.reviewCount - a.reviewCount; })
      .slice(0, YOTPO_REVIEWS_TOPN);

    if (!pool.length) {
      var emptyEl = page.querySelector('[data-shop-reviews-empty]');
      if (emptyEl) emptyEl.textContent = 'No reviews yet across this shop.';
      return;
    }

    // 1. Aggregate review count + avg.
    //    Yotpo's Cratejoy sync mirrors the SHOP total onto every product's
    //    reviews_count / reviews_average metafield (every product carries
    //    the same numbers). Summing would multiply by N (e.g. 2,873 × 25 =
    //    71,825 fake reviews). Take the MAX instead: if data is mirrored,
    //    max == the shop total; if data is ever genuinely per-product,
    //    max == the most-reviewed product (still meaningful, not inflated).
    var nonZero = data.products.filter(function (p) { return p.reviewCount > 0; });
    var totalCount = 0;
    var avg = 0;
    if (nonZero.length > 0) {
      var top = nonZero.reduce(function (a, b) {
        return a.reviewCount >= b.reviewCount ? a : b;
      });
      totalCount = top.reviewCount;
      avg = top.reviewAvg;
    }
    paintShopRating(page, avg, totalCount);

    // Per-card review count was dropped from the rating widget (stars +
    // average only), so the Yotpo mirror pattern is no longer visible to
    // the shopper. No need to hide the widget anymore.

    // 2. Fetch reviews. Page state lives at module scope so the
    //    "Load more" button can request the next page.
    var reviewsState = { page: 1, all: [], seen: {} };

    function fetchPage(pageNum) {
      return Promise.all(pool.slice(0, 6).map(function (p) {
        var url = 'https://api-cdn.yotpo.com/v1/widget/' + key
                + '/products/' + p.id + '/reviews.json?per_page=8&page=' + pageNum;
        return fetch(url)
          .then(function (r) { return r.json(); })
          .then(function (j) {
            var reviews = (j && j.response && j.response.reviews) || [];
            return reviews.map(function (rv) {
              var images = [];
              if (Array.isArray(rv.images_data)) {
                images = rv.images_data.map(function (im) {
                  return { thumb: im.thumb_url || im.original_url, full: im.original_url || im.thumb_url };
                }).filter(function (im) { return im.thumb; });
              }
              var up = rv.votes_up || 0;
              var down = rv.votes_down || 0;
              return {
                author: rv.user && (rv.user.display_name || rv.user.user_name) || 'Anonymous',
                rating: rv.score || 0,
                title: rv.title || '',
                body: rv.content || '',
                date: rv.created_at || '',
                images: images,
                product: p,
                votesUp: up,
                votesDown: down,
                helpfulScore: up - down
              };
            });
          })
          .catch(function () { return []; });
      })).then(function (lists) {
        var batch = [].concat.apply([], lists);
        var added = [];
        batch.forEach(function (r) {
          var k = (r.author || '') + '|' + (r.date || '') + '|' + (r.title || '') + '|' + String(r.body || '').slice(0, 100);
          if (reviewsState.seen[k]) return;
          reviewsState.seen[k] = 1;
          reviewsState.all.push(r);
          added.push(r);
        });
        reviewsState.all.sort(function (a, b) { return new Date(b.date) - new Date(a.date); });
        return added.length;
      });
    }

    fetchPage(1).then(function () {
      paintReviews(page, reviewsState.all.slice(0, YOTPO_REVIEWS_LIMIT));
      paintBreakdown(page, reviewsState.all);
      setupReviewsDialog(page, totalCount, reviewsState, fetchPage);
    });

    // Sort dropdown re-orders the current set. "Most helpful" ranks by
    // votes_up - votes_down (captured from Yotpo above as helpfulScore);
    // ties fall back to most-recent so shops with no helpful votes still
    // get a coherent ordering.
    var sortSel = page.querySelector('[data-shop-reviews-sort]');
    if (sortSel) {
      sortSel.addEventListener('change', function () {
        var items = Array.prototype.slice.call(
          page.querySelectorAll('[data-seller-review-row]')
        );
        var cmp = {
          recent: function (a, b) { return new Date(b.dataset.date) - new Date(a.dataset.date); },
          highest: function (a, b) { return parseFloat(b.dataset.rating) - parseFloat(a.dataset.rating); },
          lowest: function (a, b) { return parseFloat(a.dataset.rating) - parseFloat(b.dataset.rating); },
          helpful: function (a, b) {
            var diff = parseFloat(b.dataset.helpful) - parseFloat(a.dataset.helpful);
            if (diff !== 0) return diff;
            return new Date(b.dataset.date) - new Date(a.dataset.date);
          }
        }[sortSel.value];
        if (!cmp) return;
        var parent = items[0] && items[0].parentNode;
        if (parent) items.sort(cmp).forEach(function (i) { parent.appendChild(i); });
      });
    }
  }

  // ── REVIEW STAR BREAKDOWN ──────────────────────────
  // Populates the 5 server-rendered rows in [data-shop-rating-breakdown]
  // with bar widths + counts, computed from the loaded review sample
  // (reviewsState.all — a YOTPO_REVIEWS_LIMIT slice across the top
  // most-reviewed products in the shop). The distribution shown is
  // therefore a representative sample, not the absolute shop-wide total
  // (which Yotpo only exposes per-product, not aggregated across a shop).
  //
  // Clicking a row filters the visible review list to that star bucket;
  // clicking the same row again clears the filter. This makes the
  // widget actionable instead of decorative.
  function paintBreakdown(page, allReviews) {
    var wrap = page.querySelector('[data-shop-rating-breakdown]');
    if (!wrap || !allReviews || !allReviews.length) return;

    var buckets = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    allReviews.forEach(function (r) {
      var n = Math.round(r.rating || 0);
      if (n >= 1 && n <= 5) buckets[n] += 1;
    });
    var total = allReviews.length;

    var rows = wrap.querySelectorAll('[data-stars]');
    rows.forEach(function (row) {
      var stars = parseInt(row.getAttribute('data-stars'), 10);
      var count = buckets[stars] || 0;
      var pct = total > 0 ? (count / total * 100) : 0;
      var fill = row.querySelector('.seller-reviews-breakdown__fill');
      var cnt = row.querySelector('.seller-reviews-breakdown__count');
      if (fill) fill.style.width = pct.toFixed(1) + '%';
      if (cnt) cnt.textContent = count.toLocaleString();
      // Rows with zero matches are unclickable / muted so users don't
      // try to filter into an empty result set.
      if (count === 0) {
        row.setAttribute('disabled', 'disabled');
        row.style.opacity = '0.45';
        row.style.cursor = 'default';
      } else {
        row.removeAttribute('disabled');
        row.style.opacity = '';
        row.style.cursor = '';
      }
    });

    // Click-to-filter (toggle). Same handler attaches once per row;
    // guard with a flag so a re-paint doesn't double-bind.
    if (wrap._breakdownBound) return;
    wrap._breakdownBound = true;
    var activeStars = null;
    rows.forEach(function (row) {
      row.addEventListener('click', function () {
        if (row.hasAttribute('disabled')) return;
        var stars = parseInt(row.getAttribute('data-stars'), 10);
        if (activeStars === stars) {
          activeStars = null;
        } else {
          activeStars = stars;
        }
        rows.forEach(function (r) {
          r.classList.toggle('is-active', parseInt(r.getAttribute('data-stars'), 10) === activeStars);
        });
        var allRows = page.querySelectorAll('[data-seller-review-row]');
        allRows.forEach(function (rr) {
          var rating = Math.round(parseFloat(rr.dataset.rating || '0'));
          var matches = activeStars === null || rating === activeStars;
          rr.style.display = matches ? '' : 'none';
        });
      });
    });
  }

  function paintShopRating(page, avg, count) {
    if (!count) return;
    var avgRounded = Math.round(avg * 10) / 10;
    var avgFixed = avgRounded.toFixed(1);

    // Identity row
    var sumEl = page.querySelector('[data-shop-rating-summary]');
    if (sumEl) {
      sumEl.hidden = false;
      var avgEl = sumEl.querySelector('[data-shop-rating-avg]');
      var cntEl = sumEl.querySelector('[data-shop-rating-count]');
      var fillEl = sumEl.querySelector('[data-shop-rating-fill]');
      if (avgEl) avgEl.textContent = avgFixed;
      if (cntEl) cntEl.textContent = count.toLocaleString();
      if (fillEl) fillEl.style.width = (avg / 5 * 100) + '%';
    }

    // Tab count — show only when count > 0
    var tabCntEl = page.querySelector('[data-shop-review-count-tab]');
    if (tabCntEl) {
      if (count > 0) {
        tabCntEl.textContent = count.toLocaleString();
        tabCntEl.removeAttribute('data-empty');
      } else {
        tabCntEl.setAttribute('data-empty', '1');
      }
    }

    // Reviews-aside aggregate
    var aggEl = page.querySelector('[data-shop-reviews-aggregate]');
    if (aggEl) {
      aggEl.hidden = false;
      var bigEl = aggEl.querySelector('[data-shop-rating-big]');
      var asideCntEl = aggEl.querySelector('[data-shop-review-count-aside]');
      var bigFillEl = aggEl.querySelector('[data-shop-rating-big-fill]');
      if (bigEl) bigEl.textContent = avgFixed;
      if (asideCntEl) asideCntEl.textContent = count.toLocaleString() + ' reviews';
      if (bigFillEl) bigFillEl.style.width = (avg / 5 * 100) + '%';
    }

    // Items-tab "at a glance" + About-tab rating row. Each rating tile
    // sits inside a [data-glance-wrap]; if the wrapper was server-side
    // hidden (no static stats to show), reveal it now that we have
    // review data — otherwise the rating tile would render alone inside
    // an invisible parent.
    function revealGlance(tileEl) {
      if (!tileEl) return;
      tileEl.hidden = false;
      var wrap = tileEl.closest('[data-glance-wrap]');
      if (wrap && wrap.hasAttribute('hidden')) wrap.removeAttribute('hidden');
    }
    var glanceEl = page.querySelector('[data-shop-rating-glance]');
    if (glanceEl) {
      revealGlance(glanceEl);
      var glanceNum = glanceEl.querySelector('[data-shop-rating-glance-num]');
      if (glanceNum) glanceNum.textContent = avgFixed + '★';
    }
    var aboutEl = page.querySelector('[data-shop-rating-about]');
    if (aboutEl) {
      revealGlance(aboutEl);
      var aboutNum = aboutEl.querySelector('[data-shop-rating-about-num]');
      if (aboutNum) aboutNum.textContent = avgFixed;
    }
  }

  function escapeHtml(s) {
    return String(s || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function starHtml(rating) {
    var pct = Math.max(0, Math.min(100, (rating || 0) / 5 * 100));
    return '<span class="seller-card__stars">' +
             '<span class="seller-card__stars-empty">★★★★★</span>' +
             '<span class="seller-card__stars-fill" style="width:' + pct + '%;">★★★★★</span>' +
           '</span>';
  }
  // Deterministic pastel color per author name — same author keeps the
  // same avatar tint across page loads.
  function avatarColor(name) {
    var s = String(name || '?');
    var h = 0;
    for (var i = 0; i < s.length; i++) { h = (h * 31 + s.charCodeAt(i)) | 0; }
    var palette = [
      'linear-gradient(135deg, #f4d4d4 0%, #d6c2f0 100%)',
      'linear-gradient(135deg, #cfe7e7 0%, #f5d5b8 100%)',
      'linear-gradient(135deg, #f5d5e0 0%, #c8d8f2 100%)',
      'linear-gradient(135deg, #ffe2cc 0%, #fed1d1 100%)',
      'linear-gradient(135deg, #d4eedd 0%, #e2dffa 100%)',
      'linear-gradient(135deg, #fde5b8 0%, #f4c4d8 100%)'
    ];
    return palette[Math.abs(h) % palette.length];
  }

  function paintReviews(page, items) {
    var list = page.querySelector('[data-shop-reviews-items]');
    var empty = page.querySelector('[data-shop-reviews-empty]');
    if (!list) return;
    if (!items.length) {
      if (empty) empty.textContent = 'No recent reviews to show.';
      return;
    }
    if (empty) empty.remove();
    list.innerHTML = items.map(function (r) {
      var author = escapeHtml(r.author || 'Anonymous');
      var initial = escapeHtml(author.charAt(0).toUpperCase());
      var dateLabel = r.date ? new Date(r.date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '';
      var prodImg = r.product && r.product.image
        ? '<img src="' + escapeHtml(r.product.image) + '" alt="" loading="lazy" width="56" height="56">'
        : '';
      var photoStrip = '';
      if (r.images && r.images.length) {
        photoStrip = '<div class="seller-review__photos">' +
          r.images.slice(0, 6).map(function (im) {
            return '<a class="seller-review__photo" href="' + escapeHtml(im.full) + '" target="_blank" rel="noopener">' +
              '<img src="' + escapeHtml(im.thumb) + '" alt="" loading="lazy">' +
            '</a>';
          }).join('') +
        '</div>';
      }
      return '<article class="seller-review" data-seller-review-row data-date="' + escapeHtml(r.date) + '" data-rating="' + (r.rating || 0) + '" data-helpful="' + (r.helpfulScore || 0) + '">' +
        '<div class="seller-review__top">' +
          '<div class="seller-review__top-left">' +
            starHtml(r.rating) +
            '<span class="seller-review__verified" title="Verified buyer">✓ Verified</span>' +
          '</div>' +
          '<div class="seller-review__top-right">' +
            '<span class="seller-review__avatar" style="background:' + avatarColor(r.author) + ';">' + initial + '</span>' +
            '<span class="seller-review__author">' + author + '</span>' +
            (dateLabel ? '<span class="seller-review__date-sep">·</span><span class="seller-review__date">' + escapeHtml(dateLabel) + '</span>' : '') +
          '</div>' +
        '</div>' +
        (r.title ? '<h4 class="seller-review__title">' + escapeHtml(r.title) + '</h4>' : '') +
        (r.body  ? '<p class="seller-review__body">'  + escapeHtml(r.body)  + '</p>' : '') +
        photoStrip +
        '<a class="seller-review__product" href="' + escapeHtml(r.product.url) + '">' +
          '<span class="seller-review__product-img">' + prodImg + '</span>' +
          '<span class="seller-review__product-meta">' +
            '<span class="seller-review__product-lab">Item reviewed</span>' +
            '<span class="seller-review__product-title">' + escapeHtml(r.product.title) + '</span>' +
          '</span>' +
          '<span class="seller-review__product-cta">View →</span>' +
        '</a>' +
      '</article>';
    }).join('');
  }

  // "View all reviews" — opens a dialog (PDP rr-modal pattern) instead of
  // growing the page. Lazily pages Yotpo up to ALL_REVIEWS_CAP, then
  // populates the modal with a photo carousel + a scrollable reviews list.
  var ALL_REVIEWS_CAP = 30;
  var ALL_PHOTOS_CAP = 10;

  function setupReviewsDialog(page, totalCount, state, fetchPage) {
    var list = page.querySelector('[data-shop-reviews-items]');
    if (!list || !list.parentNode) return;
    if (list.parentNode.querySelector('[data-shop-reviews-view-all]')) return;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.dataset.shopReviewsViewAll = '';
    btn.className = 'seller-reviews-load-more';
    btn.textContent = 'View all reviews (' + (totalCount > 0 ? totalCount.toLocaleString() : '—') + ')';
    list.parentNode.appendChild(btn);

    var dialog = document.getElementById('seller-reviews-modal');
    if (!dialog) return;
    var backdrop = dialog.querySelector('[data-srm-backdrop]');
    var closeBtn = dialog.querySelector('[data-srm-close]');
    var photosWrap = dialog.querySelector('[data-srm-photos-wrap]');
    var photosTrack = dialog.querySelector('[data-srm-photos]');
    var photoPrev = dialog.querySelector('[data-srm-photo-prev]');
    var photoNext = dialog.querySelector('[data-srm-photo-next]');
    var reviewsSlot = dialog.querySelector('[data-srm-reviews]');

    function openDialog() {
      if (typeof dialog.showModal === 'function') dialog.showModal(); else dialog.setAttribute('open', '');
      document.body.style.overflow = 'hidden';
    }
    function closeDialog() {
      if (typeof dialog.close === 'function') dialog.close(); else dialog.removeAttribute('open');
      document.body.style.overflow = '';
    }
    if (backdrop) backdrop.addEventListener('click', closeDialog);
    if (closeBtn) closeBtn.addEventListener('click', closeDialog);
    dialog.addEventListener('cancel', function () { document.body.style.overflow = ''; });

    function renderModal() {
      // Photos — gather across all reviews, cap at 10.
      var allPhotos = [];
      state.all.some(function (r) {
        if (r.images && r.images.length) {
          r.images.forEach(function (im) {
            if (allPhotos.length < ALL_PHOTOS_CAP) allPhotos.push({ im: im, r: r });
          });
        }
        return allPhotos.length >= ALL_PHOTOS_CAP;
      });
      if (allPhotos.length > 0 && photosWrap && photosTrack) {
        photosTrack.innerHTML = allPhotos.map(function (p) {
          return '<a class="seller-rmodal__photo" href="' + escapeHtml(p.im.full) + '" target="_blank" rel="noopener">' +
            '<img src="' + escapeHtml(p.im.thumb) + '" alt="" loading="lazy">' +
          '</a>';
        }).join('');
        photosWrap.hidden = false;
      } else if (photosWrap) {
        photosWrap.hidden = true;
      }

      // Review list — cap at 30, reuse the same row markup as the inline list.
      var slice = state.all.slice(0, ALL_REVIEWS_CAP);
      if (reviewsSlot) {
        reviewsSlot.innerHTML = slice.map(function (r) {
          var author = escapeHtml(r.author || 'Anonymous');
          var initial = escapeHtml(author.charAt(0).toUpperCase());
          var dateLabel = r.date ? new Date(r.date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '';
          var prodImg = r.product && r.product.image
            ? '<img src="' + escapeHtml(r.product.image) + '" alt="" loading="lazy" width="48" height="48">'
            : '';
          var photoStrip = '';
          if (r.images && r.images.length) {
            photoStrip = '<div class="seller-review__photos">' +
              r.images.slice(0, 6).map(function (im) {
                return '<a class="seller-review__photo" href="' + escapeHtml(im.full) + '" target="_blank" rel="noopener">' +
                  '<img src="' + escapeHtml(im.thumb) + '" alt="" loading="lazy">' +
                '</a>';
              }).join('') +
            '</div>';
          }
          return '<article class="seller-review">' +
            '<div class="seller-review__top">' +
              '<div class="seller-review__top-left">' + starHtml(r.rating) +
                '<span class="seller-review__verified" title="Verified buyer">✓ Verified</span></div>' +
              '<div class="seller-review__top-right">' +
                '<span class="seller-review__avatar" style="background:' + avatarColor(r.author) + ';">' + initial + '</span>' +
                '<span class="seller-review__author">' + author + '</span>' +
                (dateLabel ? '<span class="seller-review__date-sep">·</span><span class="seller-review__date">' + escapeHtml(dateLabel) + '</span>' : '') +
              '</div>' +
            '</div>' +
            (r.title ? '<h4 class="seller-review__title">' + escapeHtml(r.title) + '</h4>' : '') +
            (r.body  ? '<p class="seller-review__body">'  + escapeHtml(r.body)  + '</p>' : '') +
            photoStrip +
            '<a class="seller-review__product" href="' + escapeHtml(r.product.url) + '">' +
              '<span class="seller-review__product-img">' + prodImg + '</span>' +
              '<span class="seller-review__product-meta">' +
                '<span class="seller-review__product-lab">Item reviewed</span>' +
                '<span class="seller-review__product-title">' + escapeHtml(r.product.title) + '</span>' +
              '</span>' +
              '<span class="seller-review__product-cta">View →</span>' +
            '</a>' +
          '</article>';
        }).join('');
      }
    }

    if (photoPrev && photosTrack) photoPrev.addEventListener('click', function () { photosTrack.scrollBy({ left: -240, behavior: 'smooth' }); });
    if (photoNext && photosTrack) photoNext.addEventListener('click', function () { photosTrack.scrollBy({ left: 240, behavior: 'smooth' }); });

    btn.addEventListener('click', function () {
      btn.disabled = true;
      var orig = btn.textContent;
      btn.textContent = 'Loading…';
      // Page through Yotpo until we hit the 30 cap or pool is exhausted.
      function pageUntilFull() {
        if (state.all.length >= ALL_REVIEWS_CAP) return Promise.resolve();
        state.page += 1;
        return fetchPage(state.page).then(function (added) {
          if (!added || added < 3 || state.all.length >= ALL_REVIEWS_CAP) return;
          return pageUntilFull();
        });
      }
      pageUntilFull().then(function () {
        btn.disabled = false;
        btn.textContent = orig;
        renderModal();
        openDialog();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
